/**
 * Cache Manager Utility
 * Helps manage localStorage, sessionStorage, and IndexedDB to prevent performance degradation
 */

const CACHE_VERSION = '1.0.0';
const CACHE_VERSION_KEY = 'app_cache_version';
const MAX_CART_HISTORY = 10; // Keep only last 10 cart states
const MAX_ADDRESS_CACHE = 20; // Keep only last 20 addresses
const CACHE_EXPIRY_DAYS = 7; // Clear old data after 7 days

class CacheManager {
  constructor() {
    this.initCleanup();
  }

  /**
   * Initialize cleanup on app start
   */
  initCleanup() {
    const currentVersion = localStorage.getItem(CACHE_VERSION_KEY);
    
    // If version mismatch, clear everything
    if (currentVersion !== CACHE_VERSION) {
      console.log('🧹 Cache version mismatch, clearing old cache...');
      this.clearAllCache();
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
    }

    // Run periodic cleanup
    this.cleanupExpiredData();
    this.optimizeLocalStorage();
  }

  /**
   * Clear all cache (localStorage, sessionStorage, IndexedDB)
   */
  clearAllCache() {
    try {
      // Keep only essential items
      const essentialKeys = [
        'restaurantId',
        'theme',
        'language',
        CACHE_VERSION_KEY
      ];
      
      const essentialData = {};
      essentialKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value) essentialData[key] = value;
      });

      // Clear localStorage
      localStorage.clear();
      
      // Restore essential data
      Object.entries(essentialData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      // Clear sessionStorage
      sessionStorage.clear();

      console.log('✅ Cache cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing cache:', error);
    }
  }

  /**
   * Clean up expired data based on timestamps
   */
  cleanupExpiredData() {
    try {
      const now = Date.now();
      const expiryMs = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      Object.keys(localStorage).forEach(key => {
        try {
          const item = localStorage.getItem(key);
          if (!item) return;

          // Check if item has timestamp
          const data = JSON.parse(item);
          if (data && data.timestamp) {
            const age = now - data.timestamp;
            if (age > expiryMs) {
              localStorage.removeItem(key);
              console.log(`🗑️ Removed expired cache: ${key}`);
            }
          }
        } catch (e) {
          // Not JSON or no timestamp, skip
        }
      });
    } catch (error) {
      console.error('Error cleaning expired data:', error);
    }
  }

  /**
   * Optimize localStorage by removing duplicate/old entries
   */
  optimizeLocalStorage() {
    try {
      // Clean up old cart snapshots
      this.cleanupCartHistory();
      
      // Clean up address cache
      this.cleanupAddressCache();
      
      // Remove temporary/debug keys
      this.removeDebugKeys();

      console.log('✅ LocalStorage optimized');
    } catch (error) {
      console.error('Error optimizing localStorage:', error);
    }
  }

  /**
   * Keep only recent cart states
   */
  cleanupCartHistory() {
    try {
      const cartKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('cart_') || key.includes('bagItems')
      );

      if (cartKeys.length > MAX_CART_HISTORY) {
        // Sort by timestamp and keep only recent
        const sorted = cartKeys
          .map(key => ({
            key,
            timestamp: this.getItemTimestamp(key)
          }))
          .sort((a, b) => b.timestamp - a.timestamp);

        // Remove old entries
        sorted.slice(MAX_CART_HISTORY).forEach(({ key }) => {
          localStorage.removeItem(key);
          console.log(`🗑️ Removed old cart: ${key}`);
        });
      }
    } catch (error) {
      console.error('Error cleaning cart history:', error);
    }
  }

  /**
   * Clean up old address cache
   */
  cleanupAddressCache() {
    try {
      const addressKeys = Object.keys(localStorage).filter(key => 
        key.includes('address') || key.includes('location')
      );

      if (addressKeys.length > MAX_ADDRESS_CACHE) {
        const sorted = addressKeys
          .map(key => ({
            key,
            timestamp: this.getItemTimestamp(key)
          }))
          .sort((a, b) => b.timestamp - a.timestamp);

        sorted.slice(MAX_ADDRESS_CACHE).forEach(({ key }) => {
          localStorage.removeItem(key);
        });
      }
    } catch (error) {
      console.error('Error cleaning address cache:', error);
    }
  }

  /**
   * Remove debug and temporary keys
   */
  removeDebugKeys() {
    const debugPatterns = ['debug_', 'temp_', 'test_', 'dev_', 'log_'];
    
    Object.keys(localStorage).forEach(key => {
      if (debugPatterns.some(pattern => key.startsWith(pattern))) {
        localStorage.removeItem(key);
        console.log(`🗑️ Removed debug key: ${key}`);
      }
    });
  }

  /**
   * Get timestamp from localStorage item
   */
  getItemTimestamp(key) {
    try {
      const item = localStorage.getItem(key);
      const data = JSON.parse(item);
      return data?.timestamp || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Store data with automatic timestamp
   */
  setItem(key, value) {
    try {
      const data = {
        value,
        timestamp: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error(`Error storing ${key}:`, error);
    }
  }

  /**
   * Get data and check expiry
   */
  getItem(key, maxAgeMs = null) {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const data = JSON.parse(item);
      
      // Check expiry if maxAgeMs provided
      if (maxAgeMs && data.timestamp) {
        const age = Date.now() - data.timestamp;
        if (age > maxAgeMs) {
          localStorage.removeItem(key);
          return null;
        }
      }

      return data.value;
    } catch {
      return null;
    }
  }

  /**
   * Get current localStorage usage in MB
   */
  getStorageSize() {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    return (total / 1024 / 1024).toFixed(2); // Convert to MB
  }

  /**
   * Check if storage is near quota
   */
  isStorageNearQuota() {
    const size = parseFloat(this.getStorageSize());
    return size > 4; // Warn if > 4MB (quota is usually 5-10MB)
  }

  /**
   * Force cleanup if near quota
   */
  forceCleanupIfNeeded() {
    if (this.isStorageNearQuota()) {
      console.warn('⚠️ Storage near quota, forcing cleanup...');
      this.clearAllCache();
    }
  }
}

// Export singleton instance
const cacheManager = new CacheManager();

export default cacheManager;

// Export utility functions
export const clearCache = () => cacheManager.clearAllCache();
export const optimizeCache = () => cacheManager.optimizeLocalStorage();
export const getStorageSize = () => cacheManager.getStorageSize();
